import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Badge , StyledP } from 'components';
import { AULink } from '../AULink';
import { Product, CustomOption, CustomOptionData } from 'types/Product';
import { adjustHiDPI, toTitleCase } from 'helpers/strings';
import { filterStateSelector, paginationStateSelector } from 'store/ui/selectors';
import { Show } from 'components/Functional';
import { useWindowSize, DeviceSize } from 'hooks/useWindowSize';
import { isNotServerSideRendering } from 'helpers/env';
import { YotpoItems, CLPFilter } from '../../types/Category';
import itly from 'itly';
import {
  FixedSizeCatalogImage,
  ProductBadge,
  ProductBlockWrapper,
  ProductDetailsWrapper,
  ProductPrice,
  ProductCalloutUl,
  ProductCalloutLi
} from './styled';
import Strikethrough from 'components/Strikethrough';
import { toAbsolutePath } from 'helpers/navigation';
import cx from 'classix';
import { VimeoVideoContainer } from 'components/Vimeo/VimeoVideoContainer';
import Player from '@vimeo/player';
import { ClpCarousel } from './ClpCarousel';
import { getPriceWithPrintingStyleFilters } from 'helpers/products';
import { getStrikethroughPrice } from 'helpers/strikethroughPricing';

interface ProductCardProps {
  product: Product;
  canonicalUrl?: string;
  shouldShowPrices?: boolean;
  isProductCalloutSplit: boolean;
  isPhotoBookCLP: boolean;
  index: number;
  yotpoData: YotpoItems[];
}

export const generateProductLivePreviewUrlWithFilters = (
  baseMediaUrl: string,
  sku: string,
  livePreviewAttributes: Array<string>,
  customOptions: Array<CustomOption>,
  defaultProductImage: string,
  currentFilters: Array<CLPFilter>
): string => {
  if (livePreviewAttributes && livePreviewAttributes.length > 0 && currentFilters.length > 0) {
    let concatenatedFiltersAttributes = '';
    livePreviewAttributes.forEach((filterType: string) => {
      const isFilterTypeBeingApplied = currentFilters.find((currentFilter: CLPFilter) => {
        const filterAttributeParsed = customOptions.find(
          (customOption: CustomOption) => customOption.name === filterType
        );

        if (filterAttributeParsed && filterAttributeParsed.filterAttribute) {
          return filterAttributeParsed.filterAttribute.toLowerCase() === currentFilter.attributeCode.toLowerCase()
            ? currentFilter
            : false;
        } else {
          return currentFilter.attributeCode.replace('filter_', '') === filterType ? currentFilter : false;
        }
      });
      if (isFilterTypeBeingApplied) {
        const foundFilterTypeInCustomOptions = customOptions.find((option: CustomOption) => {
          if (option.filterAttribute && option.filterAttribute === isFilterTypeBeingApplied.attributeCode) {
            return option.filterAttribute === isFilterTypeBeingApplied.attributeCode;
          } else {
            return option.name === isFilterTypeBeingApplied.attributeCode.replace('filter_', '');
          }
        });

        let lastFilterOptionAppliedIsAvailable = undefined;

        if (foundFilterTypeInCustomOptions) {
          lastFilterOptionAppliedIsAvailable = foundFilterTypeInCustomOptions.values.find(
            (value: CustomOptionData) =>
              (value.filterValue.toLowerCase() || value.label.toLowerCase()) ===
              isFilterTypeBeingApplied.options[isFilterTypeBeingApplied.options.length - 1].toLowerCase()
          );
        }

        if (lastFilterOptionAppliedIsAvailable) {
          concatenatedFiltersAttributes = concatenatedFiltersAttributes.concat(
            `-${lastFilterOptionAppliedIsAvailable?.label}`
          );
        } else if (
          foundFilterTypeInCustomOptions &&
          foundFilterTypeInCustomOptions.values &&
          foundFilterTypeInCustomOptions.values.length > 0
        ) {
          concatenatedFiltersAttributes = concatenatedFiltersAttributes.concat(
            `-${foundFilterTypeInCustomOptions.values[0].label}`
          );
        }
      } else {
        // We grab the first option for a given filter type from the available ones of the customOptions
        const categoryType = customOptions.find((customOption: CustomOption) => {
          return customOption.name === filterType;
        });
        concatenatedFiltersAttributes = concatenatedFiltersAttributes.concat(
          `-${categoryType.values && categoryType.values[0].label}`
        );
      }
    });
    return `${baseMediaUrl}/${sku}${concatenatedFiltersAttributes}.jpg`;
  } else {
    return defaultProductImage;
  }
};

export const isProductCategoryCard = (product: Product): boolean => product.reportingProductCategory === 'cards';

export const getYotpoProductData = (yotpoData: YotpoItems[], productId: string): YotpoItems | undefined => {
  if (yotpoData?.length > 0) {
    return yotpoData.find(item => item.productIdentifier === productId);
  }
};

const ProductCard: React.FC<ProductCardProps> = props => {
  const { filters } = useSelector(filterStateSelector);
  const { product, canonicalUrl, shouldShowPrices, isProductCalloutSplit, index } = props;
  const { productCallout, clpHoverImage, clpHoverVideoId, clpCarouselImages } = product;
  const image = product.image;
  const name = product.name;
  const href = canonicalUrl || product.urlKey;
  const badge = product.badges?.length > 0 ? product.badges[0] : null;
  const { deviceSize } = useWindowSize();
  const isMobile = deviceSize === DeviceSize.SMALL;
  const isDesktop = !isMobile;
  const { currentPage } = useSelector(paginationStateSelector);
  const oneIndexedCurrentPage = currentPage + 1;
  const priceString = getPriceWithPrintingStyleFilters(product, filters);
  const adjustedPrice = parseFloat(priceString.replace('$', ''));
  const strikethroughPrice = getStrikethroughPrice(product.strikethroughValues, adjustedPrice, product.sku);
  const [carouselImages, setCarouselImages] = useState(clpCarouselImages);

  // for en-8502 - condition to limit product callout to books
  const isBookClp = product.reportingProductCategory === 'books';
  let shouldDisplayProductCallout = false;
  const photoBookPathRegex = new RegExp('^/photo-books(?:/.*)?$');
  if (isNotServerSideRendering() && photoBookPathRegex.test(window.location.pathname)) {
    shouldDisplayProductCallout = productCallout && isBookClp && isProductCalloutSplit;
  }

  const showImageCarousel = isMobile;
  const showVideoOnHover = clpHoverVideoId && isDesktop;
  const showImageOnHover = !showVideoOnHover && isDesktop;

  const responsiveImageWidths = {
    tiny: adjustHiDPI(375), // Images are never larger than 366px
    small: adjustHiDPI(270), // Images are never larger than 263px
    medium: adjustHiDPI(280), // Images are never larger than 275px
    large: adjustHiDPI(280) // Images are never larger than 275px
  };

  const clpLinkClicked = useCallback(() => {
    itly.clpLinkClicked({
      link_type: 'card',
      page_number: oneIndexedCurrentPage,
      product_name: name,
      card_number: index + 1
    });
  }, [oneIndexedCurrentPage, name, index]);

  const altText = `${name} by Artifact Uprising | ${toTitleCase(product.reportingProductCategory)}`;

  const vimeoRef = useRef<HTMLIFrameElement>(null);

  const handleMouseEnter = () => {
    const player = new Player(vimeoRef.current);
    player.play().catch(error => {
      // PlayInterrupted error occurs when a video is paused while the play async call is still in progress
      // can happen if the user quickly hovers/removes mouse from video, so we just ignore
      if (error.name !== 'PlayInterrupted') {
        console.warn(error);
      }
    });
  };

  const handleMouseLeave = () => {
    const player = new Player(vimeoRef.current);
    player.pause();
  };

  const weAreAtPhotoCards = () =>
    (isNotServerSideRendering() && window.location.pathname.includes('photo-cards')) || false;

  useEffect(() => {
    const usingLivePreviewFilter = filters.some(
      filter => filter.attributeCode === 'filter_printing_style' || filter.attributeCode === 'filter_card_orientation'
    );

    if (weAreAtPhotoCards() && showImageCarousel && usingLivePreviewFilter) {
      const livePreviewUrlFromFilters =
        generateProductLivePreviewUrlWithFilters(
          process.env.GATSBY_LIVE_PREVIEW_URL,
          product.sku,
          product.livePreviewAttributes,
          product.customOptions,
          image,
          filters
        ) ?? clpCarouselImages[0];

      const updatedCarouselImages = [...product.clpCarouselImages];
      updatedCarouselImages[0] = livePreviewUrlFromFilters;
      setCarouselImages(updatedCarouselImages);
    } else {
      setCarouselImages(clpCarouselImages);
    }
  // TODO: Refactor this dependency
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, showImageCarousel, product]);

  return (
    <ProductBlockWrapper>
      <AULink
        id={`gatby-page-link-${product.sku}`}
        path={toAbsolutePath(href)}
        linkToGatsby={product.gatsbyPageExists}
        onClick={clpLinkClicked}
      >
        {badge && (
          <ProductBadge>
            <Badge>{badge}</Badge>
          </ProductBadge>
        )}

        <Show when={!showImageCarousel}>
          <span className={cx(isProductCategoryCard(product) && 'zoom-image-container')}>
            <FixedSizeCatalogImage
              alt={altText}
              src={
                weAreAtPhotoCards()
                  ? generateProductLivePreviewUrlWithFilters(
                      process.env.GATSBY_LIVE_PREVIEW_URL,
                      product.sku,
                      product.livePreviewAttributes,
                      product.customOptions,
                      image,
                      filters
                    )
                  : product.image
              }
              widths={responsiveImageWidths}
              className={cx(isProductCategoryCard(product) && 'zoom-image')}
            />
          </span>
        </Show>

        <Show when={showImageCarousel}>
          <ClpCarousel images={carouselImages} productName={product.name} cardIndex={index} />
        </Show>

        <Show when={showImageOnHover}>
          <span className="hover-image lazy">
            <FixedSizeCatalogImage alt={altText} src={clpHoverImage} widths={responsiveImageWidths} />
          </span>
        </Show>

        <Show when={showVideoOnHover}>
          <div className="hover-video lazy">
            <VimeoVideoContainer
              ref={vimeoRef}
              videoId={clpHoverVideoId}
              isMobile={false}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              autoplay="false"
            />
          </div>
        </Show>
      </AULink>

      <ProductDetailsWrapper style={{ alignItems: 'center' }}>
        <AULink
          id={`gatby-page-link-${product.sku}`}
          path={toAbsolutePath(href)}
          linkToGatsby={product.gatsbyPageExists}
          onClick={clpLinkClicked}
        >
          <StyledP style={{ textAlign: 'center' }}>{name}</StyledP>
        </AULink>

        {shouldShowPrices && (
          <span className={cx(showImageCarousel && 'price-link')}>
            <ProductPrice strikethrough={!!strikethroughPrice}>{priceString}</ProductPrice>

            {strikethroughPrice && (
              <Strikethrough
                strikethroughPrice={strikethroughPrice}
                shouldDisplayPromoCode={false}
                productCategory={product.reportingProductCategory}
              />
            )}
          </span>
        )}
      </ProductDetailsWrapper>

      {shouldDisplayProductCallout && (
        <ProductCalloutUl>
          {Object.keys(productCallout).map(callout => {
            const value = productCallout[callout];
            if (!value) {
              return null;
            }
            return <ProductCalloutLi key={callout}>{value}</ProductCalloutLi>;
          })}
        </ProductCalloutUl>
      )}
    </ProductBlockWrapper>
  );
};

export default ProductCard;
